// Structured, bilingual (DE/EN) content for the in-app handbook.
// Pure data only — no JSX. The HandbookDialog component renders these blocks
// and maps chapter `icon` keys to lucide-react icons.
//
// Block types supported by the renderer:
//   { type: 'p',    text }                          -> paragraph
//   { type: 'h',    text }                          -> sub-heading inside a chapter
//   { type: 'list', items: [ string | {lead, text} ] }
//   { type: 'steps', items: [ { title, text } ] }   -> numbered steps
//   { type: 'note', tone: 'info'|'warn'|'ok', title, text }
//   { type: 'contact' }                             -> renders fixed support/contact block

export const handbookContent = {
  de: {
    title: 'SwitchR-Handbuch',
    subtitle: 'Alles Wichtige zu Seiten, Geräten, Verbindungen, VLANs und Datenschutz.',
    chapters: [
      {
        id: 'quickstart',
        icon: 'rocket',
        label: 'Schnellstart',
        title: 'Schnellstart',
        intro:
          'In wenigen Schritten zum ersten Netzwerkdiagramm. SwitchR läuft vollständig im Browser – ohne Anmeldung und ohne Installation.',
        blocks: [
          {
            type: 'steps',
            items: [
              {
                title: 'Projekt anlegen',
                text: 'Öffne über „Datei → Neues Projekt“ ein leeres Diagramm oder benenne oben im Kopf das aktuelle Projekt.',
              },
              {
                title: 'Geräte platzieren',
                text: 'Ziehe Geräte aus der linken Palette auf die Zeichenfläche der Standardseite. Die Palette ist in Kategorien gegliedert: Netzwerk & IT, Haustechnik, Industrie, PV & Energie sowie Generisch & Sonstiges (neutrale Platzhalter wie „Generisches Gerät“, „Unbekannt“, IoT, USV, Rack, Standort …) für alles, was keiner Fachkategorie zugeordnet ist.',
              },
              {
                title: 'Eigenschaften setzen',
                text: 'Wähle ein Gerät aus und trage im rechten Eigenschaften-Panel Bezeichnung, Gerätetyp, Ports, VLANs und IP-Adressen ein.',
              },
              {
                title: 'Verbindungen ziehen',
                text: 'Ziehe von einem Anschlusspunkt eines Geräts zu einem anderen Gerät, um ein Kabel zu erstellen.',
              },
              {
                title: 'VLANs verwalten',
                text: 'Öffne oben die „VLAN-Tabelle“, um VLAN-IDs, Netze (IP/CIDR) und DHCP-Bereiche anzulegen. Der Design-Check prüft die Konsistenz laufend.',
              },
              {
                title: 'Detailseite ergänzen',
                text: 'Lege über die Seitenleiste unten eine Detailseite an und füge dort einzelne Geräte hinzu, um die Port-für-Port-Verkabelung darzustellen.',
              },
              {
                title: 'Exportieren',
                text: 'Exportiere dein Diagramm als nativ editierbare .drawio-Datei oder als hochwertiges PDF mit CAD-Schriftkopf.',
              },
            ],
          },
          {
            type: 'note',
            tone: 'info',
            title: 'Automatisch gespeichert',
            text: 'Deine Arbeit wird laufend im Browser-Cache gespeichert. Über „Datei → Projekt wechseln“ verwaltest du mehrere Projekte lokal.',
          },
        ],
      },
      {
        id: 'modules',
        icon: 'pages',
        label: 'Module & Seitentypen',
        title: 'Module & Seitentypen',
        intro:
          'Ein Projekt besteht aus mehreren Seiten unterschiedlicher Typen. Jeder Typ hat eine eigene Aufgabe – zusammen ergeben sie ein vollständiges Netzwerkdokument.',
        blocks: [
          { type: 'h', text: 'Standardseite' },
          {
            type: 'p',
            text: 'Die Standardseite ist die logische Übersicht deines Netzwerks. Hier platzierst du Geräte, vergibst VLANs/IPs und zeichnest Verbindungen. Sie ist die „Quelle der Wahrheit“ für alle Geräte.',
          },
          { type: 'h', text: 'Detailseite' },
          {
            type: 'p',
            text: 'Eine Detailseite zeigt ausgewählte Geräte mit ihren einzelnen Ports und der genauen Port-zu-Port-Verkabelung (z. B. Patchpläne). Geräte werden hier aus den Standardseiten übernommen – ihre Konfiguration bleibt zentral.',
          },
          { type: 'h', text: 'Positionsplan' },
          {
            type: 'p',
            text: 'Der Positionsplan verortet Geräte räumlich – etwa auf einem Grundriss, einem Foto oder einer OpenStreetMap-Karte als Hintergrund. Ideal für Standort- und Gebäudeübersichten.',
          },
          { type: 'h', text: 'Automatische Seiten' },
          {
            type: 'p',
            text: 'Kabelliste und VLAN-Liste werden automatisch aus deinen Daten erzeugt und stets aktuell gehalten. Sie sind schreibgeschützt und dienen der Dokumentation.',
          },
          {
            type: 'note',
            tone: 'info',
            title: 'Zusammenspiel',
            text: 'Geräte und ihre Konfiguration „leben“ auf den Standardseiten. Detailseiten, Positionspläne und automatische Listen greifen auf dieselben Geräte zu – Änderungen wirken überall konsistent.',
          },
        ],
      },
      {
        id: 'devices',
        icon: 'devices',
        label: 'Echte Geräte & Klone',
        title: 'Echte Geräte & Klone (Aliase)',
        intro:
          'SwitchR unterscheidet zwischen einem echten Gerät (dem Original) und Klonen (Aliassen), die dasselbe Gerät auf weiteren Seiten darstellen.',
        blocks: [
          { type: 'h', text: 'Echtes Gerät (Original)' },
          {
            type: 'p',
            text: 'Das Original trägt die gesamte Konfiguration: Bezeichnung, Gerätetyp, Ports, VLANs, IP-Adressen und Verbindungen. Es existiert genau einmal und gehört zu der Standardseite, auf der es angelegt wurde.',
          },
          { type: 'h', text: 'Klon (Alias)' },
          {
            type: 'p',
            text: 'Ein Klon ist ein Verweis auf ein bestehendes Gerät und erlaubt, dasselbe Gerät auf mehreren Seiten zu zeigen. Ein Klon speichert keine eigene Konfiguration – er spiegelt immer das Original.',
          },
          {
            type: 'list',
            items: [
              { lead: 'Gemeinsame Konfiguration:', text: 'Änderungen an Ports, VLANs oder IPs wirken auf das Original und alle seine Klone.' },
              { lead: 'Verbindungen gehören zum Original:', text: 'Kabel werden zentral am Original geführt – unabhängig davon, auf welcher Seite der Klon liegt.' },
              { lead: 'Nur die Platzierung ist lokal:', text: 'Position und Anzeige des Klons gelten nur auf der jeweiligen Seite.' },
            ],
          },
          { type: 'h', text: 'Klon anlegen' },
          {
            type: 'p',
            text: 'Nutze in der Palette den Bereich „Bestehende Geräte“ und wähle „Als Klon auf dieser Seite platzieren“. Ein Klon ist an seinem Kennzeichen erkennbar und verweist auf die Ursprungsseite des Originals.',
          },
          {
            type: 'note',
            tone: 'warn',
            title: 'Wichtig',
            text: 'Einen Klon kannst du von einer Seite entfernen, ohne das Original zu löschen. Wird jedoch das Original gelöscht, verschwinden auch alle Klone samt Verbindungen.',
          },
        ],
      },
      {
        id: 'connections',
        icon: 'cable',
        label: 'Verbindungen & Patchpanels',
        title: 'Verbindungen & Patchpanels',
        intro:
          'Verbindungen bilden die Verkabelung zwischen Geräten ab – von der logischen Übersicht bis zum Patchplan.',
        blocks: [
          { type: 'h', text: 'Verbindungen erstellen' },
          {
            type: 'p',
            text: 'Ziehe von einem Anschlusspunkt eines Geräts zum Zielgerät. Jeder Verbindung kannst du Quell- und Ziel-Port, eine Bezeichnung (z. B. Kabel-ID) und einen Verbindungstyp zuweisen.',
          },
          { type: 'h', text: 'Bündelung paralleler Verbindungen' },
          {
            type: 'p',
            text: 'Auf Standardseiten werden mehr als drei parallele Verbindungen zu einer Geräteseite hin zu einer einzigen Linie zusammengefasst und mit der Anzahl (z. B. „24“) beschriftet. So bleibt die Übersicht auch bei vielen Kabeln sauber. In Detailseiten wird jede Verbindung einzeln dargestellt.',
          },
          { type: 'h', text: 'LAG / Link-Bündel' },
          {
            type: 'p',
            text: 'Parallele Verbindungen zwischen denselben Geräten kannst du als LAG (Link Aggregation) gruppieren und benennen.',
          },
          { type: 'h', text: 'Patchpanel (Passiv)' },
          {
            type: 'p',
            text: 'Ein passives Patchpanel leitet Signale nur durch – es hat keine eigene VLAN-/IP-Konfiguration. Deshalb sind für diesen Gerätetyp alle VLAN-, IP- und Interface-Felder ausgeblendet.',
          },
          {
            type: 'list',
            items: [
              { lead: 'Beidseitige Anschlüsse:', text: 'Im Detailplan besitzt das Patchpanel Anschlusspunkte oben und unten. Verbindungen docken automatisch an der jeweils nächstgelegenen Seite an.' },
              { lead: 'Durchreiche der VLANs:', text: 'Die VLAN-Konfiguration der verbundenen aktiven Geräte wird durch das Panel „durchgereicht“ und im Verbindungsdialog angezeigt.' },
            ],
          },
          {
            type: 'note',
            tone: 'info',
            title: 'Automatisches Andocken',
            text: 'Anschlusspunkte richten sich dynamisch nach der Position des Gegenübers – auf Standard- wie Detailseiten. So bleiben Kabelwege übersichtlich, auch wenn du Geräte verschiebst.',
          },
        ],
      },
      {
        id: 'vlan',
        icon: 'network',
        label: 'VLAN-Abgleich',
        title: 'VLAN-Abgleich & Verbindungsdialog',
        intro:
          'Der Verbindungsdialog hilft, Port- und VLAN-Konfigurationen beider Enden einer Verbindung sauber abzugleichen.',
        blocks: [
          { type: 'h', text: 'Untagged & Tagged' },
          {
            type: 'p',
            text: 'Pro Port legst du ein Untagged-VLAN (Access) und beliebig viele Tagged-VLANs (Trunk) fest. Das Management-VLAN des Geräts bleibt davon unberührt.',
          },
          { type: 'h', text: 'Der Verbindungsdialog' },
          {
            type: 'p',
            text: 'Ordne beiden Geräten den passenden Anschluss zu. SwitchR vergleicht anschließend die VLAN-Konfigurationen beider Seiten.',
          },
          {
            type: 'list',
            items: [
              { lead: 'Übereinstimmung:', text: 'Stimmen beide Seiten überein, ist keine Aktion nötig.' },
              { lead: 'Abweichung:', text: 'Weichen die Konfigurationen ab, kannst du die Konfiguration einer Seite auf die andere übernehmen („Konfig A/B übernehmen“) oder bewusst unverändert lassen.' },
            ],
          },
          { type: 'h', text: 'Wann öffnet sich der Dialog?' },
          {
            type: 'p',
            text: 'Beim Erstellen einer Verbindung öffnet sich der Abgleich-Dialog nur dann automatisch, wenn tatsächlich ein VLAN-Konflikt vorliegt. Verbindungen ohne Konflikt – oder zu einem passiven Gerät ohne Gegenstelle – werden ohne Unterbrechung erstellt.',
          },
          { type: 'h', text: 'Passive Geräte im Dialog' },
          {
            type: 'p',
            text: 'Ist eine Seite ein passives Patchpanel, zeigt der Dialog die durchgereichte Konfiguration des nächsten aktiven Geräts an. Gibt es (noch) kein aktives Gerät auf der anderen Seite, steht keine Konfiguration zum Durchreichen bereit.',
          },
          {
            type: 'note',
            tone: 'ok',
            title: 'Design-Check',
            text: 'Der laufende Design-Check meldet unter anderem doppelte IPs, IPs außerhalb des Subnetzes, überlappende Netze/DHCP-Bereiche und nicht übereinstimmende Untagged-VLANs.',
          },
        ],
      },
      {
        id: 'security',
        icon: 'shield',
        label: 'Sicherheit & Datenschutz',
        title: 'Sicherheit, Datenschutz & Design',
        intro:
          'SwitchR ist „lokal zuerst“ konzipiert: Deine Daten bleiben standardmäßig auf deinem Rechner.',
        blocks: [
          { type: 'h', text: 'Lokale Speicherung' },
          {
            type: 'p',
            text: 'Projekte werden im Browser-Cache (localStorage) gespeichert – ohne Konto und ohne Server. Sie bleiben auf deinem Gerät, bis du sie löschst.',
          },
          {
            type: 'note',
            tone: 'warn',
            title: 'Hinweis zum Browser-Cache',
            text: 'Da die Speicherung im Browser erfolgt, gehen Projekte verloren, wenn du den Browser-Cache bzw. die Website-Daten löschst. Exportiere wichtige Projekte regelmäßig als .drawio-Datei.',
          },
          { type: 'h', text: 'Sicheres Teilen (Zero-Knowledge)' },
          {
            type: 'p',
            text: 'Beim „Sicher teilen“ wird das Diagramm bereits in deinem Browser Ende-zu-Ende verschlüsselt. Der Server speichert ausschließlich den verschlüsselten Inhalt – der Betreiber kann ihn nicht einsehen (Zero-Knowledge).',
          },
          {
            type: 'list',
            items: [
              { lead: 'Passwort separat übergeben:', text: 'Das Passwort wird nie übertragen. Gib es über einen getrennten, sicheren Kanal weiter (persönlich, Telefon, Passwort-Manager).' },
              { lead: 'Automatischer Ablauf:', text: 'Freigabe-Links laufen automatisch nach 30 Tagen ab.' },
              { lead: 'Zugriffsart:', text: 'Wähle zwischen Vollzugriff (bearbeitbar) und Nur-Ansicht (schreibgeschützt).' },
            ],
          },
          { type: 'h', text: 'PDF-Export & Servergrenzen' },
          {
            type: 'p',
            text: '„PDF (Browser)“ erzeugt das PDF vollständig lokal – deine Daten verlassen den Rechner nicht. Für das hochauflösende „PDF (Server, HQ)“ werden die Seiten kurzzeitig an den Server übertragen, dort nur im Arbeitsspeicher verarbeitet und nicht dauerhaft gespeichert.',
          },
          { type: 'h', text: 'draw.io-Kompatibilität' },
          {
            type: 'p',
            text: 'Exportierte .drawio-Dateien lassen sich nativ in draw.io / diagrams.net weiter bearbeiten – ohne Bindung an SwitchR.',
          },
        ],
      },
      {
        id: 'contact',
        icon: 'support',
        label: 'Kontakt & Support',
        title: 'Kontakt & Support',
        intro:
          'Fragen, Feedback oder einen Fehler entdeckt? Wir freuen uns über deine Nachricht.',
        blocks: [
          {
            type: 'p',
            text: 'SwitchR befindet sich im Beta-Test und wird laufend weiterentwickelt. Dein Feedback hilft uns, das Produkt zu verbessern.',
          },
          { type: 'contact' },
        ],
      },
    ],
  },

  en: {
    title: 'SwitchR Handbook',
    subtitle: 'Everything about pages, devices, connections, VLANs and privacy.',
    chapters: [
      {
        id: 'quickstart',
        icon: 'rocket',
        label: 'Quick start',
        title: 'Quick start',
        intro:
          'Create your first network diagram in a few steps. SwitchR runs entirely in the browser — no login, no installation.',
        blocks: [
          {
            type: 'steps',
            items: [
              { title: 'Create a project', text: 'Open a blank diagram via “File → New project” or rename the current project in the header.' },
              { title: 'Place devices', text: 'Drag devices from the left palette onto the canvas of a standard page. The palette is grouped into categories: Network & IT, Building automation, Industrial, PV & Energy and Generic & misc. (neutral placeholders such as “Generic device”, “Unknown”, IoT, UPS, rack, site …) for anything that does not fit a specialised category.' },
              { title: 'Set properties', text: 'Select a device and enter its label, device type, ports, VLANs and IP addresses in the properties panel on the right.' },
              { title: 'Draw connections', text: 'Drag from a device’s connection handle to another device to create a cable.' },
              { title: 'Manage VLANs', text: 'Open the “VLAN table” at the top to define VLAN IDs, networks (IP/CIDR) and DHCP ranges. The Design Check continuously validates consistency.' },
              { title: 'Add a detail page', text: 'Create a detail page via the sidebar at the bottom and add individual devices to show port-by-port cabling.' },
              { title: 'Export', text: 'Export your diagram as a natively editable .drawio file or as a high-quality PDF with a CAD title block.' },
            ],
          },
          {
            type: 'note',
            tone: 'info',
            title: 'Saved automatically',
            text: 'Your work is continuously saved in the browser cache. Manage multiple projects locally via “File → Switch project”.',
          },
        ],
      },
      {
        id: 'modules',
        icon: 'pages',
        label: 'Modules & page types',
        title: 'Modules & page types',
        intro:
          'A project consists of several pages of different types. Each type has its own purpose — together they form a complete network document.',
        blocks: [
          { type: 'h', text: 'Standard page' },
          { type: 'p', text: 'The standard page is the logical overview of your network. Here you place devices, assign VLANs/IPs and draw connections. It is the “source of truth” for all devices.' },
          { type: 'h', text: 'Detail page' },
          { type: 'p', text: 'A detail page shows selected devices with their individual ports and the exact port-to-port cabling (e.g. patch plans). Devices are adopted here from the standard pages — their configuration stays central.' },
          { type: 'h', text: 'Location plan' },
          { type: 'p', text: 'The location plan positions devices spatially — for example on a floor plan, a photo or an OpenStreetMap map background. Ideal for site and building overviews.' },
          { type: 'h', text: 'Automatic pages' },
          { type: 'p', text: 'The cable list and VLAN list are generated automatically from your data and kept up to date. They are read-only and serve as documentation.' },
          {
            type: 'note',
            tone: 'info',
            title: 'How they work together',
            text: 'Devices and their configuration “live” on the standard pages. Detail pages, location plans and automatic lists reference the same devices — changes apply everywhere consistently.',
          },
        ],
      },
      {
        id: 'devices',
        icon: 'devices',
        label: 'Real devices & clones',
        title: 'Real devices & clones (aliases)',
        intro:
          'SwitchR distinguishes between a real device (the original) and clones (aliases) that represent the same device on further pages.',
        blocks: [
          { type: 'h', text: 'Real device (original)' },
          { type: 'p', text: 'The original carries the entire configuration: label, device type, ports, VLANs, IP addresses and connections. It exists exactly once and belongs to the standard page on which it was created.' },
          { type: 'h', text: 'Clone (alias)' },
          { type: 'p', text: 'A clone is a reference to an existing device and lets you show the same device on multiple pages. A clone stores no configuration of its own — it always mirrors the original.' },
          {
            type: 'list',
            items: [
              { lead: 'Shared configuration:', text: 'Changes to ports, VLANs or IPs apply to the original and all of its clones.' },
              { lead: 'Connections belong to the original:', text: 'Cables are managed centrally on the original — regardless of which page the clone is on.' },
              { lead: 'Only placement is local:', text: 'The clone’s position and appearance apply only to the respective page.' },
            ],
          },
          { type: 'h', text: 'Create a clone' },
          { type: 'p', text: 'In the palette, use the “Existing devices” section and choose “Place as a clone on this page”. A clone is recognizable by its badge and references the original’s source page.' },
          {
            type: 'note',
            tone: 'warn',
            title: 'Important',
            text: 'You can remove a clone from a page without deleting the original. However, if the original is deleted, all clones and their connections disappear too.',
          },
        ],
      },
      {
        id: 'connections',
        icon: 'cable',
        label: 'Connections & patch panels',
        title: 'Connections & patch panels',
        intro: 'Connections represent the cabling between devices — from the logical overview to the patch plan.',
        blocks: [
          { type: 'h', text: 'Creating connections' },
          { type: 'p', text: 'Drag from a device’s connection handle to the target device. For each connection you can assign a source and target port, a label (e.g. cable ID) and a connection type.' },
          { type: 'h', text: 'Bundling parallel connections' },
          { type: 'p', text: 'On standard pages, more than three parallel connections toward one device side are collapsed into a single line labelled with the count (e.g. “24”). This keeps the overview clean even with many cables. On detail pages every connection is shown individually.' },
          { type: 'h', text: 'LAG / link bundle' },
          { type: 'p', text: 'You can group parallel connections between the same devices as a LAG (link aggregation) and name them.' },
          { type: 'h', text: 'Patch panel (passive)' },
          { type: 'p', text: 'A passive patch panel merely passes signals through — it has no VLAN/IP configuration of its own. For this device type all VLAN, IP and interface fields are therefore hidden.' },
          {
            type: 'list',
            items: [
              { lead: 'Connections on both sides:', text: 'In the detail plan the patch panel has connection points at the top and bottom. Connections automatically dock to the nearest side.' },
              { lead: 'VLAN pass-through:', text: 'The VLAN configuration of the connected active devices is “passed through” the panel and shown in the connection dialog.' },
            ],
          },
          {
            type: 'note',
            tone: 'info',
            title: 'Automatic docking',
            text: 'Connection points align dynamically to the position of the counterpart — on standard and detail pages alike. This keeps cable routes tidy even when you move devices.',
          },
        ],
      },
      {
        id: 'vlan',
        icon: 'network',
        label: 'VLAN matching',
        title: 'VLAN matching & connection dialog',
        intro: 'The connection dialog helps you cleanly reconcile the port and VLAN configuration of both ends of a connection.',
        blocks: [
          { type: 'h', text: 'Untagged & tagged' },
          { type: 'p', text: 'For each port you set one untagged VLAN (access) and any number of tagged VLANs (trunk). The device’s management VLAN is unaffected by this.' },
          { type: 'h', text: 'The connection dialog' },
          { type: 'p', text: 'Assign the matching port on each device. SwitchR then compares the VLAN configurations of both sides.' },
          {
            type: 'list',
            items: [
              { lead: 'Match:', text: 'If both sides match, no action is required.' },
              { lead: 'Mismatch:', text: 'If the configurations differ, you can adopt one side’s configuration onto the other (“Adopt config A/B”) or deliberately keep them as they are.' },
            ],
          },
          { type: 'h', text: 'When does the dialog open?' },
          { type: 'p', text: 'When creating a connection, the reconciliation dialog only opens automatically if there is an actual VLAN conflict. Connections without a conflict — or to a passive device without a counterpart — are created without interruption.' },
          { type: 'h', text: 'Passive devices in the dialog' },
          { type: 'p', text: 'If one side is a passive patch panel, the dialog shows the passed-through configuration of the nearest active device. If there is (as yet) no active device on the other side, there is no configuration to pass through.' },
          {
            type: 'note',
            tone: 'ok',
            title: 'Design Check',
            text: 'The live Design Check reports, among other things, duplicate IPs, IPs outside the subnet, overlapping networks/DHCP ranges and mismatched untagged VLANs.',
          },
        ],
      },
      {
        id: 'security',
        icon: 'shield',
        label: 'Security & privacy',
        title: 'Security, privacy & design',
        intro: 'SwitchR is designed “local first”: by default your data stays on your computer.',
        blocks: [
          { type: 'h', text: 'Local storage' },
          { type: 'p', text: 'Projects are stored in the browser cache (localStorage) — without an account and without a server. They stay on your device until you delete them.' },
          {
            type: 'note',
            tone: 'warn',
            title: 'Note on the browser cache',
            text: 'Because storage happens in the browser, projects are lost if you clear the browser cache or website data. Export important projects regularly as a .drawio file.',
          },
          { type: 'h', text: 'Secure sharing (zero-knowledge)' },
          { type: 'p', text: 'When you “share securely”, the diagram is end-to-end encrypted already in your browser. The server stores only the encrypted content — the operator cannot read it (zero-knowledge).' },
          {
            type: 'list',
            items: [
              { lead: 'Share the password separately:', text: 'The password is never transmitted. Pass it on via a separate, secure channel (in person, phone, password manager).' },
              { lead: 'Automatic expiry:', text: 'Share links expire automatically after 30 days.' },
              { lead: 'Access type:', text: 'Choose between full access (editable) and view only (read-only).' },
            ],
          },
          { type: 'h', text: 'PDF export & server boundaries' },
          { type: 'p', text: '“PDF (Browser)” generates the PDF entirely locally — your data never leaves your computer. For the high-resolution “PDF (Server, HQ)”, the pages are briefly sent to the server, processed in memory only and not stored permanently.' },
          { type: 'h', text: 'draw.io compatibility' },
          { type: 'p', text: 'Exported .drawio files can be edited natively in draw.io / diagrams.net — with no lock-in to SwitchR.' },
        ],
      },
      {
        id: 'contact',
        icon: 'support',
        label: 'Contact & support',
        title: 'Contact & support',
        intro: 'Questions, feedback or found a bug? We’d love to hear from you.',
        blocks: [
          { type: 'p', text: 'SwitchR is in beta testing and is continuously being improved. Your feedback helps us make the product better.' },
          { type: 'contact' },
        ],
      },
    ],
  },
};
